import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Device, type Ended, Factors, type Providers } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, EmptyState, ListRow, Waiting } from '@beyond-js/ui/react';
import { Panel, Problem, Retry, Unsent, removed, useUnread } from '@accounts/ui';
import { Ending, type Reach } from './ending';

/**
 * Where this account is signed in, by device (ACC-07): each sign-in with its browser and system, an
 * approximate place, when it was last used and the products it opened, as the family's rows. One can be
 * signed out on its own; "Sign out of other sessions" keeps this browser; signing out everywhere stays
 * the last resort. Both may also end what acts for the person, their AI apps and access tokens (D196).
 */
export function Devices() {
	const { text, date, client, accounts, settle, renewed, language } = usePresence();
	const factors = useMemo(() => new Factors(client), [client]);
	const [offered, setOffered] = useState<Providers>();
	const [devices, setDevices] = useState<Device[]>();
	// When they were last read, said over them while a later read does not answer
	const [at, setAt] = useState<number>();
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [notice, setNotice] = useState<string>();
	const [busy, setBusy] = useState(false);
	const [unsent, setUnsent] = useState<string>();
	const [reach, setReach] = useState<Reach>();

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(async () => {
		setUnread(Refusal.kept);
		try {
			setOffered(await accounts.providers());
			setDevices(await factors.devices());
			setAt(Date.now());
			setUnread(undefined);
			return true;
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
			setUnread(given);
			return !given.unreachable;
		}
	}, [accounts, factors]);
	// Accounts not answering is the page's to say once; this section says it in its place (D163)
	const outage = useUnread({ key: 'devices', title: text('devices.title'), unread, run: read });
	useEffect(() => {
		void read();
	}, [read, renewed]);

	const label = (device: Device) => (device.browser && device.system ? text('devices.on', { browser: device.browser, system: device.system }) : (device.browser ?? device.system ?? text('devices.unknown')));
	const product = (id: string) => offered?.products.find(entry => entry.id === id)?.name ?? id;

	async function act(work: () => Promise<{ ended: number; access?: Ended }>, target: string) {
		setBusy(true);
		setFailure(undefined);
		setUnsent(undefined);
		try {
			const done = await work();
			const said = text('devices.ended', { count: done.ended });
			setNotice(done.access ? `${said} ${removed(text, done.access)}` : said);
			await read();
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return setUnsent(error.unsent ? target : undefined);
			setFailure(error);
			// Nothing came back, so the sessions may have ended: the list says what is true now
			if (Refusal.is(error) && error.unknown) await read();
		} finally {
			setBusy(false);
		}
	}

	/** What the confirmation was accepted for, with whether what acts for the person ends too */
	async function accept(access: boolean) {
		const accepted = reach;
		setReach(undefined);
		if (accepted === 'others') await act(() => factors.others(access), 'others');
		if (accepted === 'everywhere') await leave(access);
	}

	async function leave(access: boolean) {
		setFailure(undefined);
		try {
			await accounts.leaveEverywhere(access);
			await settle(true);
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return setUnsent(error.unsent ? 'everywhere' : undefined);
			setFailure(error);
			if (Refusal.is(error) && error.unknown) await settle(true);
		}
	}

	return (
		<Panel
			id="devices"
			title={text('devices.title')}
			updated={outage && devices ? (at ?? null) : undefined}
			actions={
				devices && devices.length > 1 ? (
					<Button variant="secondary" busy={busy} onClick={() => setReach('others')}>
						{text('devices.others')}
					</Button>
				) : undefined
			}
		>
			{failure ? <Problem error={failure} check={read} /> : null}
			{notice ? <Callout tone="success" live title={notice} /> : null}
			{/* The list waits in place, and says so in place when Accounts does not answer it (D163); signing out
			    everywhere needs no list and stays drawn (LOAD-01) */}
			{!devices && !unread ? <Waiting language={language} /> : null}
			{outage && !devices ? <EmptyState kind="outage" labels={EmptyState.labels[language]} /> : null}
			{unread && !outage ? <Retry title={text(unread.key)} label={text('action.retry')} busy={text('state.retrying')} onRetry={read} error={unread} /> : null}
			<Unsent end shown={unsent === 'others'} />
			{devices?.length ? (
				<ul className="bui-rows" data-framed aria-label={text('devices.title')}>
					{devices.map(device => (
						<ListRow
							key={device.handle}
							title={label(device)}
							meta={[
								device.current ? text('devices.current') : null,
								device.place ?? text('devices.place.unknown'),
								text('devices.seen', { date: date(device.seen) }),
								text('devices.signed', { date: date(device.signed) }),
								device.products.length ? text('devices.opened', { products: device.products.map(entry => product(entry.audience)).join(', ') }) : null
							]}
							menu={device.current ? [] : [{ label: text('devices.signout'), onSelect: () => void act(() => factors.close(device.handle), device.handle) }]}
						/>
					))}
				</ul>
			) : null}
			<Unsent end shown={Boolean(unsent && devices?.some(device => device.handle === unsent))} />
			<div className="mt-4 border-t border-line pt-3">
				<Button variant="quiet" onClick={() => setReach('everywhere')}>
					{text('action.signout.everywhere')}
				</Button>
				<Unsent end shown={unsent === 'everywhere'} />
			</div>
			<Ending reach={reach} onCancel={() => setReach(undefined)} onAccept={access => void accept(access)} />
		</Panel>
	);
}
